import React, { useContext, useEffect, useState } from "react";

import { ReactComponent as AddSvg } from "../../../assets/icons/add.svg";
import { ReactComponent as SwitchSvg } from "../../../assets/icons/switch.svg";

import ItemList from "./ItemList";
import Modal from "./Modal";
import useCertificates from "../../../hooks/useCertificates";
import ProfileContext from "../ProfileContext";
import { format } from "date-fns";
import AppContext from "../../../AppContext";
import { langComponent } from "../../../constants";
import useScreenSize from "../../../hooks/useScreenSize";
import Button from "../../common/Button";
import useCompanies from "../../../hooks/useCompanies";

const CertificationList = (props) => {
  const [toggleModal, setToggleModal] = useState(false);
  const [dbdFile, setDbdFile] = useState(undefined);
  const [certificates, setCertificates] = useState(undefined);
  const [isHasMore, setIsHasMore] = useState(false);
  const { queryCertificate } = useCertificates();
  const {
    user: profileUser,
    isOwner,
    isCompany,
    company,
    isOwnerCompany,
    viewMode,
  } = useContext(ProfileContext);
  const { user, plans, getLang, isAllowed, displayImages } =
    useContext(AppContext);
  const Translation = getLang(langComponent.certificate);
  const TranslationCommon = getLang(langComponent.common);
  const [currentPage, setCurrentPage] = useState(1);
  const [isToggleCertificate, setIsToggleCertificate] = useState(false);
  const { screenSize } = useScreenSize();
  const { uploadCompanyDBD } = useCompanies();

  useEffect(() => {
    fetchData(1, true);

    if (isCompany && company?.dbd?.file) {
      setDbdFile(company.dbd.file);
    }
  }, []);

  useEffect(() => {
    if (["xs", "sm"].includes(screenSize)) {
      setIsToggleCertificate(false);
      return;
    }

    return setIsToggleCertificate(true);
  }, [screenSize]);

  const fetchData = async (page, reset) => {
    let queryFilter = {
      status: isOwner ? { $gt: 0 } : 2,
      active: true,
      deleted: false,
    };

    if (isCompany) {
      queryFilter.company = company?._id;
    }

    if (!isCompany) {
      queryFilter.user = profileUser?._id;
      queryFilter.company = null;
    }

    const {
      data,
      _metadata: { totalPages },
    } = await queryCertificate(queryFilter, page, 3, {
      name: 1,
      issuer: 1,
      effectiveDate: 1,
      dueDate: 1,
      status: 1,
    });

    const _certificates = data.map(
      ({ _id, name, issuer, effectiveDate, dueDate, status }) => {
        return {
          _id,
          name,
          issuer,
          period: `${format(new Date(effectiveDate), "MMM yyyy")}${
            dueDate ? `- ${format(new Date(dueDate), "MMM yyyy")}` : ""
          }`,
          status,
        };
      }
    );

    setIsHasMore(totalPages > page);
    setCertificates((curr) => {
      if (!curr || reset) {
        return _certificates;
      }

      return [...curr, ..._certificates];
    });
  };

  const handleAddClick = () => {
    setToggleModal(true);
  };

  const handleModalClose = () => {
    setToggleModal(false);
  };

  const handleModalSuccess = () => {
    setToggleModal(false);
    fetchData(1, true);
  };

  const handleSeeMoreClick = () => {
    let newPage = currentPage + 1;
    setCurrentPage(newPage);
    fetchData(newPage);
  };

  const handleToggleClick = () => {
    setIsToggleCertificate((curr) => !curr);
  };

  const getCertificatesAction = () => {
    if (viewMode === "guest") {
      return <></>;
    }

    if (!isCompany && isOwner) {
      return (
        <AddSvg
          className="profile-card-certs-header__add"
          onClick={handleAddClick}
        />
      );
    }

    if (isCompany && isAllowed("CM-04-0000", "create")) {
      return (
        <AddSvg
          className="profile-card-certs-header__add"
          onClick={handleAddClick}
        />
      );
    }

    return <></>;
  };

  const handleDBDFileChanged = async ({ target: { files } }) => {
    if (files.length <= 0) {
      return;
    }

    const response = await uploadCompanyDBD(company._id, files[0]);

    setDbdFile(response.dbd.file);
  };

  const getDBDAction = () => {
    if (!isCompany || viewMode === "guest") {
      return <></>;
    }

    if (isAllowed("CM-04-0000", "create")) {
      return (
        <>
          <label className="profile-card-certs-header__add" for="dbd-uploader">
            {!dbdFile ? <AddSvg width={16} /> : <SwitchSvg width={18} />}
            <input
              type="file"
              id="dbd-uploader"
              style={{ display: "none" }}
              accept="image/*"
              multiple={false}
              onChange={handleDBDFileChanged}
            />
          </label>
        </>
      );
    }

    return <></>;
  };

  const renderList = () => {
    const _isMobile = ["xs", "sm"].includes(screenSize);

    if (!isOwner && _isMobile && !isToggleCertificate) return <></>;

    return (
      <div className="profile-card-certs-lists">
        {(certificates ?? []).map((cert) => {
          if (!isOwner && cert.status !== 2) return <></>;

          return (
            <ItemList
              key={cert._id}
              id={cert._id}
              item={cert}
              onModalSuccess={handleModalSuccess}
            />
          );
        })}
      </div>
    );
  };

  const isNotAllowedCertificates =
    !isCompany &&
    isOwner &&
    ["Business", "Enterprise"].includes(
      plans.find((p) => p._id === user.plan).name
    );

  const isHasCerts = certificates && certificates.length > 0;

  return (
    <>
      {isCompany && (isOwner || (!isOwner && dbdFile)) && (
        <div className="profile-card-certs">
          <div className="profile-card-certs-header">
            <h3 className="profile-card-certs-header__title">
              {Translation("DBD")}
            </h3>
            {getDBDAction()}
          </div>
          {dbdFile && (
            <div className="profile-card-certs-dbd">
              <img
                className="profile-card-certs-dbd__image"
                src={dbdFile}
                alt="dbd"
                onClick={() => displayImages([dbdFile], 0)}
              />
              {/* <Button label="Submit for Verification" variant={"secondary"} /> */}
            </div>
          )}
        </div>
      )}
      {((isOwner && !isNotAllowedCertificates) ||
        (!isOwner &&
          isHasCerts &&
          certificates.filter((c) => c.status === 2).length > 0)) && (
        <div className="profile-card-certs">
          <div className="profile-card-certs-header">
            <h3 className="profile-card-certs-header__title">
              {Translation("Certifications")}
            </h3>
            {getCertificatesAction()}
            {!isOwner && ["xs", "sm"].includes(screenSize) && (
              <span
                className="profile-card-certs-header__toggler"
                onClick={handleToggleClick}
              >
                {isToggleCertificate ? "Hide" : "Show"}
              </span>
            )}
          </div>
          {renderList()}
          {isHasMore && (
            <div
              className="profile-card-certs-see-more"
              onClick={handleSeeMoreClick}
            >
              {TranslationCommon("See more")}
            </div>
          )}
        </div>
      )}
      {toggleModal && (
        <Modal
          mode="add"
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}
    </>
  );
};

export default CertificationList;
